// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { fetchQuery, graphql } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import type { issueDetailsForAIContextPanelQuery } from '@atlassian/jira-relay/src/__generated__/issueDetailsForAIContextPanelQuery.graphql';
// eslint-disable-next-line jira/import-whitelist
import { metrics } from '@atlassian/browser-metrics';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
} from '@atlassian/react-resource-router';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import { RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS } from '../../constants.tsx';
import type { IssueDetails } from './types.tsx';

const ISSUE_DETAILS_AGG_METRIC = 'context-panel-issue-details-agg';
const issueDetailsAggMetric = metrics.custom({
	key: ISSUE_DETAILS_AGG_METRIC,
});

export const getIssueDetails = async (
	issueKey: string,
	cloudId: string,
	includeContextPanelState = false,
): Promise<IssueDetails> => {
	issueDetailsAggMetric.start();
	const issueDetails = await fetchQuery<issueDetailsForAIContextPanelQuery>(
		getRelayEnvironment(),
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query issueDetailsForAIContextPanelQuery(
				$issueKey: String!
				$cloudId: ID!
				$includeContextPanelState: Boolean!
			) {
				jira @optIn(to: "JiraIssueFieldByIdOrAlias") {
					issueByKey(key: $issueKey, cloudId: $cloudId) {
						issueId
						fieldByIdOrAlias(idOrAlias: "servicedesk-practices") {
							... on JiraSingleLineTextField {
								text
							}
						}
						projectField {
							project {
								isAIEnabled
								projectId
								isAiContextFeatureEnabled @include(if: $includeContextPanelState)
							}
						}
					}
				}
			}
		`,
		{
			issueKey,
			cloudId,
			includeContextPanelState,
		},
		{ fetchPolicy: 'store-or-network' },
	)
		.toPromise()
		.then((data) => {
			issueDetailsAggMetric.stop();
			return data;
		})
		.catch(() => {
			issueDetailsAggMetric.cancel();
			return null; // only returning null instead of throwing error since it polluted overall SSR success rate
		});

	return {
		issueId: issueDetails?.jira?.issueByKey?.issueId ?? undefined,
		practice: issueDetails?.jira?.issueByKey?.fieldByIdOrAlias?.text ?? undefined,
		projectId: issueDetails?.jira?.issueByKey?.projectField?.project?.projectId ?? undefined,
		isAIEnabled: issueDetails?.jira?.issueByKey?.projectField?.project?.isAIEnabled ?? undefined,
		...(includeContextPanelState && {
			isAiContextFeatureEnabled:
				issueDetails?.jira?.issueByKey?.projectField?.project?.isAiContextFeatureEnabled ??
				undefined,
		}),
	};
};

export const contextPanelIssueDetailsResource = createResource<IssueDetails | null>({
	type: RESOURCE_TYPE_CONTEXT_PANEL_ISSUE_DETAILS,
	getKey: ({ match, query }) => {
		const issueKey = match.params.issueKey || query.selectedIssue || '';

		return `contextPanelIssueDetailsResource-${issueKey}`;
	},
	getData: async ({ match, query }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
		const issueKey = match.params.issueKey || query.selectedIssue || '';
		const cloudId = tenantContext.cloudId;

		if (!checkLicense(tenantContext, 'servicedesk')) {
			return null;
		}

		if (!issueKey) {
			/* only returning null instead of throwing error since it polluted overall SSR success rate */
			/* until a better is implemented */
			return null;
		}

		return getIssueDetails(issueKey, cloudId, true);
	},
	maxAge: 60 * 60 * 1000,
});
